import { useCallback, useState } from "react";

type ButtonProps = {
  "aria-expanded": boolean;
  "aria-controls"?: string;
  onClick: (event: React.MouseEvent) => void;
};

type DisclosureProps = {
  hidden: boolean;
  id?: string;
};

export type UseDisclosureReturn = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onToggle: () => void;
  isControlled: boolean;
  getButtonProps: <T extends Record<string, unknown>>(
    props?: T
  ) => T & ButtonProps;
  getDisclosureProps: <T extends Record<string, unknown>>(
    props?: T
  ) => T & DisclosureProps;
};

export interface UseDisclosureProps {
  isOpen?: boolean;
  defaultIsOpen?: boolean;
  onClose?(): void;
  onOpen?(): void;
  id?: string;
}

export const useDisclosure = (
  props: UseDisclosureProps = {}
): UseDisclosureReturn => {
  const {
    isOpen: isOpenProp,
    defaultIsOpen,
    onClose: onCloseProp,
    onOpen: onOpenProp,
    id,
  } = props;

  // Chakra v3 uses 'open' instead of 'isOpen', so we need to map
  const [internalOpen, setInternalOpen] = useState(defaultIsOpen ?? false);
  const isControlled = isOpenProp !== undefined;
  const isOpen = isControlled ? isOpenProp ?? false : internalOpen;

  const onOpen = useCallback(() => {
    if (!isControlled) {
      setInternalOpen(true);
    }
    onOpenProp?.();
  }, [isControlled, onOpenProp]);

  const onClose = useCallback(() => {
    if (!isControlled) {
      setInternalOpen(false);
    }
    onCloseProp?.();
  }, [isControlled, onCloseProp]);

  const onToggle = useCallback(() => {
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  }, [isOpen, onClose, onOpen]);

  const getButtonProps = useCallback(
    <T extends Record<string, unknown>>(props: T = {} as T) => ({
      ...props,
      "aria-expanded": isOpen,
      "aria-controls": id,
      onClick: (event: React.MouseEvent) => {
        if (props.onClick && typeof props.onClick === "function") {
          (props.onClick as (event: React.MouseEvent) => void)(event);
        }
        onToggle();
      },
    }),
    [isOpen, id, onToggle]
  );

  const getDisclosureProps = useCallback(
    <T extends Record<string, unknown>>(props: T = {} as T) => ({
      ...props,
      hidden: !isOpen,
      id,
    }),
    [isOpen, id]
  );

  return {
    isOpen,
    onOpen,
    onClose,
    onToggle,
    isControlled,
    getButtonProps,
    getDisclosureProps,
  };
};

export default useDisclosure;
